/**
 * FX Manager
 * 
 * Unified facade for all visual effects:
 * - Screen ripple distortion (post-process)
 * - Camera shake (world-space via CameraController)
 * - Time ramps (slow-motion via GameTime)
 * - Particles (via ParticleManager)
 * 
 * Does NOT listen to events - called directly by CombatSystem with FXConfig.
 */

import { Scene, Camera, Vector3 } from '@babylonjs/core';
import { logger } from '../../core/Logger';
import { gameTime } from '../../core/GameTime';
import { ScreenRippleEffect, RippleEffectConfig } from './ScreenRippleEffect';
import { CameraShakeEffect, ShakeEffectConfig } from './CameraShakeEffect';
import type { FXConfig } from './FXPresets';
import type { ParticleManager } from '../ParticleManager';

export class FXManager {
  private rippleEffect: ScreenRippleEffect;
  private shakeEffect: CameraShakeEffect;
  private particleManager: ParticleManager | null;

  constructor(
    scene: Scene,
    camera: Camera,
    particleManager?: ParticleManager
  ) {
    this.particleManager = particleManager ?? null;
    
    // Create sub-effects
    this.rippleEffect = new ScreenRippleEffect(scene, camera);
    this.shakeEffect = new CameraShakeEffect();
    
    logger.debug.log('✅ FXManager initialized');
  }

  /**
   * Spawn FX at a world position using an FXConfig.
   * 
   * @example
   * // From preset
   * fxManager.spawnFX(fromFXPreset('heavy_hit'), position);
   * 
   * @example
   * // Custom config
   * fxManager.spawnFX({
   *   ripple: { amplitude: 0.03, duration: 0.3 },
   *   shake: { amplitude: 0.1, duration: 0.2 }
   * }, position);
   * 
   * @example
   * // Preset with overrides
   * fxManager.spawnFX({
   *   ...fromFXPreset('melee_hit'),
   *   timeRamp: undefined // No slow-mo
   * }, position);
   */
  public spawnFX(config: FXConfig, position: Vector3): void {
    if (!config) return;
    
    const intensity = config.intensity ?? 1.0;
    
    console.log(`🎆 FXManager.spawnFX at ${position.toString()}`, config);
    
    // Trigger ripple effect
    if (config.ripple) {
      this.triggerRipple(position, {
        amplitude: config.ripple.amplitude * intensity,
        duration: config.ripple.duration,
        frequency: config.ripple.frequency,
        speed: config.ripple.speed,
        timeRamp: config.ripple.timeRamp,
      });
    }
    
    // Trigger camera shake
    if (config.shake) {
      this.triggerShake({
        amplitude: config.shake.amplitude * intensity,
        duration: config.shake.duration,
        frequency: config.shake.frequency,
        timeRamp: config.shake.timeRamp,
      });
    }
    
    // Trigger time ramp
    if (config.timeRamp) {
      this.triggerTimeRamp(
        config.timeRamp.targetScale,
        config.timeRamp.rampDuration,
        config.timeRamp.holdDuration,
        config.timeRamp.easing
      );
    }
    
    // Trigger particles
    if (config.particles && this.particleManager) {
      this.particleManager.spawnOneShot(config.particles, position, {
        scale: intensity,
      });
    }
  }

  /**
   * Trigger a ripple effect at a world position
   */
  public triggerRipple(position: Vector3, config?: Partial<Omit<RippleEffectConfig, 'centerX' | 'centerY'>>): string {
    const screenPos = this.rippleEffect.worldToScreen(position);
    return this.rippleEffect.trigger({
      centerX: screenPos.x,
      centerY: screenPos.y,
      ...config,
    });
  }

  /**
   * Trigger a camera shake effect
   */
  public triggerShake(config?: ShakeEffectConfig): string {
    return this.shakeEffect.trigger(config);
  }

  /**
   * Trigger a time ramp effect (slow-motion punch)
   */
  public triggerTimeRamp(
    targetScale: number,
    rampDuration: number,
    holdDuration?: number,
    _easing?: string
  ): void {
    // Ramp down to slow-mo
    gameTime.rampTo(targetScale, rampDuration, 'punch', () => {
      // After hold duration, ramp back to normal
      if (holdDuration && holdDuration > 0) {
        setTimeout(() => {
          gameTime.rampTo(1.0, rampDuration * 2, 'easeOut');
        }, holdDuration * 1000);
      } else {
        // Immediate ramp back
        gameTime.rampTo(1.0, rampDuration * 2, 'easeOut');
      }
    });
  }

  /**
   * Get the CameraShakeEffect instance (for CameraController integration)
   */
  public getCameraShakeEffect(): CameraShakeEffect {
    return this.shakeEffect;
  }

  /**
   * Get the ScreenRippleEffect instance
   */
  public getScreenRippleEffect(): ScreenRippleEffect {
    return this.rippleEffect;
  }

  /**
   * Update all effects. Call every frame.
   * @param deltaTime Real delta time (not scaled)
   */
  public update(deltaTime: number): void {
    this.rippleEffect.update(deltaTime);
    this.shakeEffect.update(deltaTime);
  }

  /**
   * Check if any effects are currently active
   */
  public hasActiveEffects(): boolean {
    return this.rippleEffect.hasActiveEffects() || this.shakeEffect.hasActiveEffects();
  }

  /**
   * Cancel all active effects
   */
  public cancelAll(): void {
    this.rippleEffect.cancelAll();
    this.shakeEffect.cancelAll();
  }

  /**
   * Dispose all resources
   */
  public dispose(): void {
    this.rippleEffect.dispose();
    this.shakeEffect.dispose();
    
    logger.debug.log('🗑️ FXManager disposed');
  }
}
